import { useState, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useTicketsByClient } from "@/hooks/use-tickets";
import {
  useRobotsClients,
  useCreateRobotClient,
  useUpdateRobotClient,
  useDeleteRobotClient,
  useRobotClientSavHistory,
  useRobotModelImages,
} from "@/hooks/use-robots-clients";
import {
  usePuduShopsLiables,
  usePuduShopDetail,
  useLierPuduShop,
  useDelierPuduShop,
} from "@/hooks/use-pudu-shop-link";
import { supabase } from "@/lib/supabase";
import { authFetch } from "@/lib/authFetch";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Client, Ticket, RobotClient, Facture } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/status-badge";
import { CopyButton } from "@/components/CopyButton";
import { PhotoUploadSlot } from "@/components/photo-upload-slot";
import { EntiteIntervenants } from "@/components/ContactLiens";
import { ActionsLiees } from "@/components/actions-liees";
import {
  Mail,
  Bot,
  ArrowLeft,
  Upload,
  Loader2,
  Send,
  BadgeCheck,
  Plus,
  Eye,
  Pencil,
  Trash2,
  TicketCheck,
  ExternalLink,
  Camera,
  FileText,
  UserCheck,
  Key,
  Clock,
  ShieldCheck,
  AlertTriangle,
  ChevronDown,
  LogIn,
  Store,
  Link2,
  Unlink,
  RefreshCw,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useClientPermissions } from "./use-client-permissions";
import { ClientAvatar, contractLabels, fetchLogoForDomain } from "./client-shared";
import {
  getRobotClientStatus,
  ROBOT_CLIENT_STATUSES,
  ROBOT_CLIENT_STATUS_VALUES,
} from "@shared/robotStatusConfig";
import { canDo } from "@shared/roles";

const ticketStatusLabels: Record<string, string> = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente_piece: "En attente pièce",
  resolu: "Résolu",
  ferme: "Fermé",
};

const robotModels = [
  "CC1", "CC1 Pro", "BellaBot", "PuduBot2", "FlashBot", "T300", "KettyBot", "MT1", "SH1",
];

const emptyRobotForm = {
  modele: "",
  numeroSerie: "",
  macAddress: "",
  dateAchat: "",
  dateInstallation: "",
  garantieFin: "",
  statut: ROBOT_CLIENT_STATUS_VALUES.ACTIF as string,
  notes: "",
};

// ── ClientDetailContent ───────────────────────────────────────────────────────

export function ClientDetailContent({
  client,
  technicians,
}: {
  client: Client;
  technicians: any[];
}) {
  const { data: clientTickets = [] } = useTicketsByClient(client.id);
  const { data: clientRobots = [], isLoading: robotsLoading } = useRobotsClients(client.id);
  const { data: modelImages = {} } = useRobotModelImages();
  const createRobotMutation = useCreateRobotClient();
  const updateRobotMutation = useUpdateRobotClient();
  const deleteRobotMutation = useDeleteRobotClient();
  const tickets: Ticket[] = clientTickets;
  const installerTech = technicians.find((t: any) => t.id === client.installerTechnicianId);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { canSendBienvenue, canValidate } = useClientPermissions();

  const [emailBienvenueModal, setEmailBienvenueModal] = useState(false);
  const [emailBienvenueSending, setEmailBienvenueSending] = useState(false);
  const [clientBienvenueAt, setClientBienvenueAt] = useState<string | null>(
    (client as any).emailBienvenueEnvoyeAt ?? null
  );

  const handleSendBienvenue = async () => {
    setEmailBienvenueSending(true);
    try {
      const res = await authFetch(`/api/clients/${client.id}/email-bienvenue`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur inconnue");
      setClientBienvenueAt(data.sentAt);
      setEmailBienvenueModal(false);
      toast({ title: `Email envoyé à ${data.to}` });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    } catch (err: any) {
      toast({ title: "Erreur d'envoi", description: err.message, variant: "destructive" });
    } finally {
      setEmailBienvenueSending(false);
    }
  };

  const [declarationStatutLocal, setDeclarationStatutLocal] = useState<string | null>(
    (client as any).declarationStatut ?? null
  );
  const [validationSending, setValidationSending] = useState(false);
  const showValidationButton =
    canValidate && (client as any).ficheACompleter && declarationStatutLocal !== "validee";

  const handleValiderDeclaration = async () => {
    setValidationSending(true);
    try {
      const res = await authFetch(`/api/clients/${client.id}/declaration/valider`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur inconnue");
      setDeclarationStatutLocal("validee");
      if (data.sentAt) setClientBienvenueAt(data.sentAt);
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({
        title: "Fiche validée",
        description: data.sentAt
          ? "Mail de bienvenue envoyé au client."
          : "Fiche marquée comme complète.",
      });
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    } finally {
      setValidationSending(false);
    }
  };

  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(
    (client as any).logoUrl || null
  );
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch(`/api/clients/${client.id}/logo`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error("Erreur upload logo");
      const updated = await res.json();
      setCurrentLogoUrl(updated.logoUrl || null);
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Logo mis à jour" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const [robotFormOpen, setRobotFormOpen] = useState(false);
  const [robotForm, setRobotForm] = useState({ ...emptyRobotForm });
  const [editingRobot, setEditingRobot] = useState<RobotClient | null>(null);
  const [viewingRobot, setViewingRobot] = useState<RobotClient | null>(null);

  const handleCreateRobot = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createRobotMutation.mutateAsync({
        clientId: client.id,
        modele: robotForm.modele,
        numeroSerie: robotForm.numeroSerie,
        macAddress: robotForm.macAddress || null,
        dateAchat: robotForm.dateAchat || null,
        dateInstallation: robotForm.dateInstallation || null,
        garantieFin: robotForm.garantieFin || null,
        statut: robotForm.statut,
        notes: robotForm.notes || null,
      });
      toast({ title: "Robot ajouté" });
      setRobotFormOpen(false);
      setRobotForm({ ...emptyRobotForm });
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    }
  };

  const handleEditRobot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRobot) return;
    try {
      await updateRobotMutation.mutateAsync({
        id: editingRobot.id,
        clientId: client.id,
        modele: robotForm.modele,
        numeroSerie: robotForm.numeroSerie,
        macAddress: robotForm.macAddress || null,
        dateAchat: robotForm.dateAchat || null,
        dateInstallation: robotForm.dateInstallation || null,
        garantieFin: robotForm.garantieFin || null,
        statut: robotForm.statut,
        notes: robotForm.notes || null,
      });
      toast({ title: "Robot modifié" });
      setEditingRobot(null);
      setViewingRobot(null);
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    }
  };

  const openEditRobot = (robot: RobotClient) => {
    setRobotForm({
      modele: robot.modele,
      numeroSerie: robot.numeroSerie,
      macAddress: robot.macAddress || "",
      dateAchat: robot.dateAchat || "",
      dateInstallation: robot.dateInstallation || "",
      garantieFin: robot.garantieFin || "",
      statut: robot.statut,
      notes: robot.notes || "",
    });
    setViewingRobot(null);
    setEditingRobot(robot);
  };

  const handleDeleteRobot = async (robotId: string) => {
    try {
      await deleteRobotMutation.mutateAsync({ id: robotId, clientId: client.id });
      toast({ title: "Robot supprimé" });
      setViewingRobot(null);
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    }
  };

  if (viewingRobot) {
    return (
      <RobotDetailView
        robot={viewingRobot}
        onBack={() => setViewingRobot(null)}
        onEdit={openEditRobot}
        onDelete={handleDeleteRobot}
      />
    );
  }

  if (editingRobot) {
    return (
      <div className="space-y-4 pt-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditingRobot(null)}
            data-testid="btn-back-from-edit-robot"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <span className="font-medium">Modifier le robot</span>
        </div>
        {renderRobotForm(
          robotForm,
          setRobotForm,
          handleEditRobot,
          updateRobotMutation.isPending,
          "Enregistrer"
        )}
      </div>
    );
  }

  if (robotFormOpen) {
    return (
      <div className="space-y-4 pt-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setRobotFormOpen(false);
              setRobotForm({ ...emptyRobotForm });
            }}
            data-testid="btn-back-from-add-robot"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <span className="font-medium">Ajouter un robot</span>
        </div>
        {renderRobotForm(
          robotForm,
          setRobotForm,
          handleCreateRobot,
          createRobotMutation.isPending,
          "Ajouter"
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-2 text-sm">
      {/* Logo + upload */}
      <div className="flex items-start gap-4 pb-1">
        <div className="relative group shrink-0">
          <ClientAvatar
            logoUrl={currentLogoUrl}
            companyName={(client as any).nomCommercial || client.companyName}
            size={72}
          />
          {canSendBienvenue && (
            <label
              className="absolute inset-0 cursor-pointer rounded-lg opacity-0 group-hover:opacity-100 bg-black/50 flex items-center justify-center transition-opacity"
              title="Changer le logo"
              data-testid="btn-upload-client-logo"
            >
              {uploadingLogo ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Upload className="w-5 h-5 text-white" />
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
              />
            </label>
          )}
        </div>
        <div className="flex-1">
          <div className="font-semibold text-base">
            {(client as any).nomCommercial || client.companyName}
          </div>
          {(client as any).nomCommercial &&
            client.companyName !== (client as any).nomCommercial && (
              <div className="text-xs text-muted-foreground">{client.companyName}</div>
            )}
          {(client as any).numeroClient != null ? (
            <div className="text-xs font-mono font-semibold text-foreground">
              N° {(client as any).numeroClient}
            </div>
          ) : (
            client.clientNumber && (
              <div className="text-xs font-mono text-muted-foreground">
                N° {client.clientNumber}
              </div>
            )
          )}
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <span className="truncate">{client.address}</span>
            <CopyButton value={client.address || ""} ariaLabel="Copier l'adresse" size="sm" />
          </div>
        </div>
      </div>

      <div>
        <span className="text-muted-foreground">SIREN :</span> {client.siren || "—"}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-muted-foreground">Secteur :</span> {client.sector || "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Groupe :</span> {client.groupName || "—"}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Adresse :</span>
        <span>{client.address}</span>
        <CopyButton value={client.address || ""} ariaLabel="Copier l'adresse" size="sm" />
      </div>
      <div>
        <span className="text-muted-foreground">Contact :</span> {client.contactCivility}{" "}
        {client.contactName}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Tél :</span> {client.contactPhone}
          <CopyButton value={client.contactPhone || ""} ariaLabel="Copier le numéro" size="sm" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Email :</span> {client.contactEmail}
          <CopyButton value={client.contactEmail || ""} ariaLabel="Copier l'email" size="sm" />
        </div>
      </div>

      {showValidationButton && (
        <div className="flex items-center gap-3 py-1 mb-1">
          <Button
            variant="default"
            size="sm"
            className="h-8 bg-violet-600 hover:bg-violet-700 text-white"
            onClick={handleValiderDeclaration}
            disabled={validationSending}
            data-testid="btn-valider-declaration"
          >
            {validationSending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Validation…
              </>
            ) : (
              <>
                <BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
                Valider la fiche & envoyer le mail de bienvenue
              </>
            )}
          </Button>
        </div>
      )}

      {canSendBienvenue && (
        <div className="flex items-center gap-3 py-1">
          {clientBienvenueAt ? (
            <>
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-green-500" />
                Email de bienvenue envoyé le{" "}
                {format(new Date(clientBienvenueAt), "d MMM yyyy", { locale: fr })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 px-2 text-muted-foreground"
                onClick={() => setEmailBienvenueModal(true)}
                data-testid="btn-renvoyer-bienvenue"
              >
                Renvoyer
              </Button>
            </>
          ) : (
            <Button
              variant="default"
              size="sm"
              className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setEmailBienvenueModal(true)}
              data-testid="btn-envoyer-bienvenue"
            >
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              Envoyer l'email de bienvenue
            </Button>
          )}
        </div>
      )}

      <Dialog open={emailBienvenueModal} onOpenChange={setEmailBienvenueModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-blue-500" />
              Email de bienvenue
            </DialogTitle>
            <DialogDescription>
              Vous allez envoyer l'email de bienvenue à{" "}
              <strong>{client.contactName}</strong> ({client.contactEmail}).
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setEmailBienvenueModal(false)}
              disabled={emailBienvenueSending}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSendBienvenue}
              disabled={emailBienvenueSending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-confirm-bienvenue"
            >
              {emailBienvenueSending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Envoi...
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Envoyer
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-muted-foreground">Contrat :</span>{" "}
          {client.contractType ? contractLabels[client.contractType] : "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Leaser :</span> {client.leaser || "—"}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-muted-foreground">Début contrat :</span>{" "}
          {client.contractStartDate || "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Fin contrat :</span>{" "}
          {client.contractEndDate || "—"}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-muted-foreground">Technicien installateur :</span>{" "}
          {installerTech ? `${installerTech.firstName} ${installerTech.lastName}` : "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Contrat de maintenance :</span>{" "}
          {client.maintenanceContract ? "Oui" : "Non"}
        </div>
      </div>
      <div>
        <span className="text-muted-foreground">Environnement :</span>{" "}
        {client.environmentType || "—"}
      </div>
      {client.notes && (
        <div>
          <span className="text-muted-foreground">Notes :</span> {client.notes}
        </div>
      )}

      <EntiteIntervenants entiteType="client" entiteId={client.id} />

      {/* Robots */}
      <div className="border-t pt-3 mt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Robots ({clientRobots.length})</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRobotFormOpen(true);
              setRobotForm({ ...emptyRobotForm });
            }}
            data-testid="btn-add-robot"
          >
            <Plus className="h-3 w-3 mr-1" /> Ajouter un robot
          </Button>
        </div>
        {robotsLoading ? (
          <p className="text-muted-foreground text-xs">Chargement...</p>
        ) : clientRobots.length === 0 ? (
          <p className="text-muted-foreground text-xs">Aucun robot associé</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modèle</TableHead>
                <TableHead>N° de série</TableHead>
                <TableHead>MAC</TableHead>
                <TableHead>Date d'achat</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientRobots.map((robot: RobotClient) => {
                const modelImg = robot.imageUrl || modelImages[robot.modele] || null;
                return (
                  <TableRow key={robot.id} data-testid={`robot-row-${robot.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {modelImg ? (
                          <img
                            src={modelImg}
                            alt={robot.modele}
                            className="w-10 h-10 rounded-lg object-cover border shadow-sm shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center border shadow-sm shrink-0">
                            <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                        )}
                        <span className="font-medium">{robot.modele}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{robot.numeroSerie}</TableCell>
                    <TableCell className="font-mono text-xs">{robot.macAddress || "—"}</TableCell>
                    <TableCell>
                      {robot.dateAchat
                        ? format(new Date(robot.dateAchat), "dd/MM/yyyy", { locale: fr })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <StatusBadge
                          status={robot.statut}
                          type="robotClient"
                          label={getRobotClientStatus(robot.statut).label}
                        />
                        {(robot as any).aCompleter && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            data-testid={`badge-a-completer-robot-${robot.id}`}
                          >
                            À compléter
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingRobot(robot)}
                        data-testid={`btn-view-robot-${robot.id}`}
                      >
                        <Eye className="h-3 w-3 mr-1" /> Voir fiche
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Tickets */}
      <div className="border-t pt-3 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <TicketCheck className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Tickets ({tickets.length})</span>
        </div>
        {tickets.length === 0 ? (
          <p className="text-muted-foreground text-xs">Aucun ticket associé</p>
        ) : (
          <div className="space-y-1">
            {tickets.map((t, index) => {
              const ticketId = typeof t.id === "string" && t.id.trim() ? t.id : null;
              const ticketReference = typeof t.ticketNumber === "string" ? t.ticketNumber.trim() : "";
              const ticketContent = (
                <>
                  <span className="font-mono text-xs">{t.ticketNumber || ""}</span>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      status={t.status}
                      type="ticket"
                      label={ticketStatusLabels[t.status]}
                    />
                    <span className="text-xs text-muted-foreground">
                      {t.receivedAt
                        ? format(new Date(t.receivedAt), "dd/MM/yyyy", { locale: fr })
                        : ""}
                    </span>
                  </div>
                </>
              );

              if (!ticketId || !ticketReference) {
                return (
                  <div
                    key={t.id || `ticket-${index}`}
                    className="flex items-center justify-between py-1 px-2 rounded bg-muted/30"
                    data-testid={`client-ticket-${t.id || `fallback-${index}`}`}
                  >
                    {ticketContent}
                  </div>
                );
              }

              return (
                <Link
                  key={t.id}
                  href={`/tickets?openId=${encodeURIComponent(ticketId)}`}
                  className="flex items-center justify-between py-1 px-2 rounded bg-muted/30 cursor-pointer hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Ouvrir le ticket ${ticketReference}`}
                  data-testid={`client-ticket-${t.id}`}
                >
                  {ticketContent}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <PuduShopLinkSection client={client} />

      <FacturesSection clientId={client.id} />

      <EspaceClientSection clientId={client.id} clientEmail={(client as any).contactEmail} />

      <div className="border-t pt-3 mt-3">
        <ActionsLiees emetteurType="client" emetteurId={client.id} />
      </div>
    </div>
  );
}

// ── EspaceClientSection ───────────────────────────────────────────────────────

interface EspaceClientAccount {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: "admin" | "contact";
  status: string;
  activationPending?: boolean;
  welcomeEmailSentAt: string | null;
  lastLoginAt: string | null;
  mustChangePassword: boolean;
}

interface EspaceClientStatus {
  hasAccount: boolean;
  accounts: EspaceClientAccount[];
}

interface EmailLog {
  id: string;
  emailType: string;
  sentAt: string;
  sentByName: string | null;
  metadata: Record<string, any> | null;
}

interface ClientProfileChange {
  kind: string;
  changedAt: string;
  authorName: string;
  authorEmail: string;
}

function EspaceClientSection({
  clientId,
  clientEmail,
}: {
  clientId: string;
  clientEmail?: string | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [showLogs, setShowLogs] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "contact" as "admin" | "contact",
  });
  const [creating, setCreating] = useState(false);
  const [accountAction, setAccountAction] = useState<string | null>(null);
  const createLock = useRef(false);
  const accountActionLock = useRef(false);
  const isAdmin = canDo(profile?.accessRole);
  const { data: status, isLoading, isError: statusError } = useQuery<EspaceClientStatus>({
    queryKey: ["/api/clients", clientId, "espace-client", "status", showRemoved],
    queryFn: async () => {
      const response = await authFetch(`/api/clients/${clientId}/espace-client/status${showRemoved ? "?includeRemoved=true" : ""}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Impossible de charger les comptes espace client.");
      return data as EspaceClientStatus;
    },
  });
  const activeAdmins = status?.accounts.filter((account) => account.role === "admin" && account.status === "active" && !account.activationPending).length ?? 0;
  const totalActiveAdmins = status?.accounts.filter((account) => account.role === "admin" && account.status === "active").length ?? 0;
  const { data: lastProfileChange } = useQuery<ClientProfileChange | null>({
    queryKey: ["/api/clients", clientId, "espace-client", "profile-changes"],
    queryFn: async () => {
      const response = await authFetch(`/api/clients/${clientId}/espace-client/profile-changes`);
      if (!response.ok) throw new Error("Impossible de charger la dernière modification du profil société.");
      return response.json();
    },
  });

  const { data: logs = [], isError: logsError, isLoading: logsLoading } = useQuery<EmailLog[]>({
    queryKey: ["/api/clients", clientId, "espace-client", "email-logs"],
    queryFn: async () => {
      const response = await authFetch(`/api/clients/${clientId}/espace-client/email-logs`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Impossible de charger l'historique des emails.");
      return data as EmailLog[];
    },
    enabled: showLogs,
  });

  const invalidateAccountQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["/api/clients", clientId, "espace-client", "status"],
    });
    queryClient.invalidateQueries({
      queryKey: ["/api/clients", clientId, "espace-client", "email-logs"],
    });
  };

  const handleCreateAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAdmin || createLock.current) return;
    createLock.current = true;
    setCreating(true);
    try {
      const response = await authFetch(`/api/clients/${clientId}/espace-client/accounts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: newAccount.firstName.trim(),
          lastName: newAccount.lastName.trim(),
          email: newAccount.email.trim().toLowerCase(),
          role: newAccount.role,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.code === "INVITATION_SEND_FAILED") invalidateAccountQueries();
        if (response.status === 409) {
          throw new Error(data.message || "Cette adresse email est déjà utilisée par un autre compte client.");
        }
        throw new Error(data.message || "Impossible de créer ce compte.");
      }
      toast({ title: "Compte créé", description: `Invitation envoyée à ${data.to || newAccount.email.trim()}.` });
      setNewAccount({ firstName: "", lastName: "", email: "", role: "contact" });
      setCreateOpen(false);
      invalidateAccountQueries();
    } catch (error: any) {
      toast({
        title: "Erreur de création",
        description: error?.message || "Impossible de créer ce compte.",
        variant: "destructive",
      });
    } finally {
      createLock.current = false;
      setCreating(false);
    }
  };

  const handleAccountAction = async (
    account: EspaceClientAccount,
    action: "invitation" | "activate" | "deactivate" | "remove",
  ) => {
    if (!isAdmin || accountActionLock.current) return;
    accountActionLock.current = true;
    const actionKey = `${account.id}:${action}`;
    setAccountAction(actionKey);
    try {
      const isInvitation = action === "invitation";
      const isRemoval = action === "remove";
      const response = await authFetch(
        isInvitation
          ? `/api/clients/${clientId}/espace-client/accounts/${account.id}/invitation`
          : isRemoval
            ? `/api/clients/${clientId}/espace-client/accounts/${account.id}`
          : `/api/clients/${clientId}/espace-client/accounts/${account.id}/status`,
        {
          method: isInvitation ? "POST" : isRemoval ? "DELETE" : "PATCH",
          ...(isInvitation || isRemoval
            ? {}
            : {
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: action === "activate" ? "active" : "inactive" }),
              }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(data.message || "Cette adresse email est déjà utilisée par un autre compte client.");
        }
        throw new Error(data.message || "Impossible de mettre à jour ce compte.");
      }
      toast({
        title: isInvitation
          ? `Invitation envoyée à ${data.to || account.email}`
          : action === "activate" ? "Compte réactivé" : isRemoval ? "Accès retiré" : "Compte désactivé",
      });
      invalidateAccountQueries();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error?.message || "Impossible de mettre à jour ce compte.",
        variant: "destructive",
      });
    } finally {
      accountActionLock.current = false;
      setAccountAction(null);
    }
  };

  const handleRoleChange = async (account: EspaceClientAccount, role: "admin" | "contact") => {
    if (!isAdmin || accountActionLock.current || account.role === role) return;
    accountActionLock.current = true;
    setAccountAction(`${account.id}:role`);
    try {
      const response = await authFetch(`/api/clients/${clientId}/espace-client/accounts/${account.id}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Impossible de modifier le rôle.");
      toast({ title: "Rôle mis à jour" });
      invalidateAccountQueries();
    } catch (error: any) {
      toast({ title: "Erreur", description: error?.message || "Impossible de modifier le rôle.", variant: "destructive" });
    } finally {
      accountActionLock.current = false;
      setAccountAction(null);
    }
  };

  const formatAccountDate = (date: string | null, missing: string) =>
    date ? format(new Date(date), "d MMM yyyy 'à' HH:mm", { locale: fr }) : missing;

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-violet-500" />
          <span className="font-medium">Accès espace client ({status?.accounts.filter(a => a.status !== "removed").length ?? 0})</span>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => {
              setNewAccount({ firstName: "", lastName: "", email: clientEmail || "", role: "contact" });
              setCreateOpen(true);
            }}
            disabled={creating}
          >
            <Send className="h-3.5 w-3.5" />
            Inviter un contact
          </Button>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={(open) => { if (!creating) setCreateOpen(open); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Inviter à l'espace client</DialogTitle>
            <DialogDescription>L'invitation sera envoyée uniquement à l'adresse saisie ci-dessous.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAccount} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="invite-client-first">Prénom</Label>
                <Input id="invite-client-first" value={newAccount.firstName} onChange={e => setNewAccount({ ...newAccount, firstName: e.target.value })} required disabled={creating} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="invite-client-last">Nom</Label>
                <Input id="invite-client-last" value={newAccount.lastName} onChange={e => setNewAccount({ ...newAccount, lastName: e.target.value })} required disabled={creating} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-client-email">Email</Label>
              <Input id="invite-client-email" type="email" value={newAccount.email} onChange={e => setNewAccount({ ...newAccount, email: e.target.value })} required disabled={creating} />
            </div>
            <div className="space-y-1">
              <Label>Rôle espace client</Label>
              <Select value={newAccount.role} onValueChange={(role: "admin" | "contact") => setNewAccount({ ...newAccount, role })} disabled={creating}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setCreateOpen(false)} disabled={creating}>Annuler</Button>
              <Button type="submit" size="sm" disabled={creating}>
                {creating ? "Envoi…" : "Envoyer l'invitation"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Chargement...</p>
      ) : statusError ? (
        <p className="text-xs text-destructive">Impossible de charger les comptes espace client.</p>
      ) : !status?.accounts.length ? (
        <div className="flex flex-col items-center justify-center py-6 text-center gap-2 bg-muted/20 rounded-xl border border-dashed border-border/60">
          <UserCheck className="w-7 h-7 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">Aucun accès espace client configuré pour ce client.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {status.accounts.map((account) => {
            const name = [account.firstName, account.lastName].filter(Boolean).join(" ") || "Nom non renseigné";
            const rowBusy = accountAction?.startsWith(`${account.id}:`) ?? false;
            const lastActiveAdmin = account.role === "admin" && account.status === "active" &&
              (totalActiveAdmins <= 1 || (!account.activationPending && activeAdmins <= 1));
            const lastAdminReason = "Dernier administrateur actif — désignez ou réactivez un autre administrateur avant de le désactiver.";
            return (
              <div
                key={account.id}
                className="rounded-lg border bg-secondary/30 px-3 py-2.5 space-y-2"
                data-testid={`client-account-${account.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{name}</div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="break-all">{account.email}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && account.status !== "removed" ? (
                      <span title={lastActiveAdmin ? lastAdminReason : "Changer le rôle de ce compte"}>
                        <Select
                          value={account.role}
                          onValueChange={(role: "admin" | "contact") => handleRoleChange(account, role)}
                          disabled={!!accountAction || lastActiveAdmin}
                        >
                          <SelectTrigger className="h-7 w-[120px] text-xs" aria-label={`Rôle de ${account.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="contact">Contact</SelectItem>
                          </SelectContent>
                        </Select>
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${account.role === "admin" ? "border border-violet-400 text-violet-600" : "border border-slate-300 text-slate-500"}`}>
                        {account.role === "admin" ? "Admin" : "Contact"}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                      account.status === "active"
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    }`}
                    >
                      {account.status === "active" ? "Actif" : account.status === "removed" ? "Accès retiré" : "Désactivé"}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Key className="h-3.5 w-3.5" />
                    Invitation : {formatAccountDate(account.welcomeEmailSentAt, "Jamais envoyée")}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <LogIn className="h-3.5 w-3.5" />
                    Dernière connexion : {formatAccountDate(account.lastLoginAt, "Jamais connecté")}
                  </span>
                </div>

                {account.mustChangePassword && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <Key className="h-3.5 w-3.5" />
                    <span>Activation ou changement de mot de passe en attente</span>
                  </div>
                )}

                {isAdmin && account.status !== "removed" && (
                  <div className="flex flex-wrap gap-2 border-t pt-2">
                    {account.status === "active" ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => handleAccountAction(account, "invitation")}
                          disabled={!!accountAction}
                          data-testid={`btn-send-account-invitation-${account.id}`}
                        >
                          {rowBusy && accountAction?.endsWith(":invitation")
                            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            : <Send className="h-3.5 w-3.5 mr-1" />}
                          Renvoyer l’invitation
                        </Button>
                        <span title={lastActiveAdmin ? lastAdminReason : undefined}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs text-destructive"
                            onClick={() => handleAccountAction(account, "deactivate")}
                            disabled={!!accountAction || lastActiveAdmin}
                            data-testid={`btn-deactivate-client-account-${account.id}`}
                          >
                            {rowBusy && accountAction?.endsWith(":deactivate")
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <ShieldAlert className="h-3.5 w-3.5 mr-1" />}
                            Désactiver
                          </Button>
                        </span>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => handleAccountAction(account, "activate")}
                          disabled={!!accountAction}
                          data-testid={`btn-reactivate-client-account-${account.id}`}
                        >
                          {rowBusy && accountAction?.endsWith(":activate")
                            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                          Réactiver
                        </Button>
                      </>
                    )}
                    <span title={lastActiveAdmin ? lastAdminReason : undefined}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-destructive"
                        onClick={() => {
                          if (window.confirm(`Retirer définitivement l'accès de ${account.email} ? Le compte sera masqué et ses traces conservées.`)) {
                            handleAccountAction(account, "remove");
                          }
                        }}
                        disabled={!!accountAction || lastActiveAdmin}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Retirer l'accès
                      </Button>
                    </span>
                  </div>
                )}
                {isAdmin && lastActiveAdmin && (
                  <p className="text-xs text-amber-700 dark:text-amber-400" role="note">{lastAdminReason}</p>
                )}
              </div>
            );
          })}

        </div>
      )}
      {!isLoading && !statusError && (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={() => setShowRemoved(v => !v)}>
            {showRemoved ? "Masquer les accès retirés" : "Afficher les accès retirés"}
          </Button>
        </div>
      )}
      <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground"
              onClick={() => setShowLogs((value) => !value)}
              data-testid="btn-toggle-email-logs"
            >
              <ChevronDown className={`h-3.5 w-3.5 mr-1 transition-transform ${showLogs ? "rotate-180" : ""}`} />
              Historique emails ({logs.length})
            </Button>
          </div>

          {showLogs && (
            <div className="space-y-1 mt-1">
              {logsLoading ? (
                <p className="text-xs text-muted-foreground">Chargement de l’historique…</p>
              ) : logsError ? (
                <p className="text-xs text-destructive">Impossible de charger l’historique des emails.</p>
              ) : logs.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun email envoyé.</p>
              ) : (
                logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between text-xs px-3 py-1.5 rounded bg-secondary/20"
                    data-testid={`email-log-${log.id}`}
                  >
                    <span className="text-muted-foreground">
                      {log.emailType === "invitation"
                        ? "Invitation espace client"
                        : log.emailType === "reset"
                          ? "Réinitialisation du mot de passe"
                          : log.emailType}
                    </span>
                    <div className="flex items-center gap-2 text-muted-foreground/70">
                      {log.metadata?.to && <span>{String(log.metadata.to)}</span>}
                      {log.sentByName && <span>{log.sentByName}</span>}
                      <span>
                        {format(new Date(log.sentAt), "d MMM yyyy HH:mm", { locale: fr })}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
      {lastProfileChange && (
        <p className="text-xs text-muted-foreground mt-2">
          Dernière modification par le client : {lastProfileChange.authorName || lastProfileChange.authorEmail}
          {" — "}{formatAccountDate(lastProfileChange.changedAt, "Date inconnue")}
          {" · "}{lastProfileChange.kind}
        </p>
      )}
    </div>
  );
}

// ── PuduShopLinkSection ───────────────────────────────────────────────────────

function PuduShopLinkSection({ client }: { client: Client }) {
  const { canManage } = useClientPermissions();
  const puduShopId = (client as any).puduShopId as string | null | undefined;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelier, setConfirmDelier] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);

  const { data: currentShop, isLoading: loadingCurrentShop } = usePuduShopDetail(puduShopId);
  const { data: liablesData, isLoading: loadingLiables } = usePuduShopsLiables(
    search,
    dialogOpen
  );
  const lierMutation = useLierPuduShop(client.id);
  const delierMutation = useDelierPuduShop(client.id);
  const { toast } = useToast();

  const handleOpenDialog = () => {
    setSelectedShopId(null);
    setSearch("");
    setDialogOpen(true);
  };

  const handleConfirmLier = () => {
    if (!selectedShopId) return;
    lierMutation.mutate(
      { shopId: selectedShopId, replace: !!puduShopId },
      {
        onSuccess: (data) => {
          toast({
            title: puduShopId ? "Magasin remplacé" : "Magasin lié",
            description: data.shopName || data.shopId,
          });
          setDialogOpen(false);
        },
        onError: (e: any) => {
          toast({ title: "Erreur", description: e.message, variant: "destructive" });
        },
      }
    );
  };

  const handleDelier = () => {
    delierMutation.mutate(undefined, {
      onSuccess: () => {
        toast({ title: "Magasin délié" });
        setConfirmDelier(false);
      },
      onError: (e: any) => {
        toast({ title: "Erreur", description: e.message, variant: "destructive" });
      },
    });
  };

  if (!canManage && !puduShopId) return null;

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Store className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Magasin Pudu</span>
      </div>

      {puduShopId ? (
        <div
          className="flex items-center justify-between gap-2 py-1.5 px-3 rounded bg-muted/30"
          data-testid="pudu-shop-linked"
        >
          <div className="flex flex-col min-w-0">
            {loadingCurrentShop ? (
              <span className="text-xs text-muted-foreground">Chargement...</span>
            ) : (
              <>
                <span className="text-sm font-medium truncate">
                  {currentShop?.shopName || puduShopId}
                </span>
                <span className="text-xs text-muted-foreground">
                  ID: {puduShopId}
                  {typeof currentShop?.nbRobots === "number"
                    ? ` · ${currentShop.nbRobots} robot(s)`
                    : ""}
                </span>
              </>
            )}
          </div>
          {canManage && (
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleOpenDialog}
                data-testid="btn-changer-pudu-shop"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Changer
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs text-destructive"
                onClick={() => setConfirmDelier(true)}
                data-testid="btn-delier-pudu-shop"
              >
                <Unlink className="h-3.5 w-3.5 mr-1" />
                Délier
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="py-1">
          {canManage ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={handleOpenDialog}
              data-testid="btn-lier-pudu-shop"
            >
              <Link2 className="h-3.5 w-3.5 mr-1" />
              Lier au magasin Pudu
            </Button>
          ) : (
            <p className="text-muted-foreground text-xs">Aucun magasin Pudu lié</p>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-lier-pudu-shop">
          <DialogHeader>
            <DialogTitle>
              {puduShopId ? "Changer le magasin Pudu" : "Lier au magasin Pudu"}
            </DialogTitle>
            <DialogDescription>
              Recherchez un magasin (périmètre FR8030) non encore rattaché à un client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou identifiant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                data-testid="input-search-pudu-shop"
              />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
              {loadingLiables ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Chargement...
                </div>
              ) : (liablesData?.items?.length || 0) === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Aucun magasin liable trouvé
                </div>
              ) : (
                liablesData!.items.map((shop) => (
                  <button
                    type="button"
                    key={shop.shopId}
                    onClick={() => setSelectedShopId(shop.shopId)}
                    className={`w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors ${
                      selectedShopId === shop.shopId ? "bg-muted" : ""
                    }`}
                    data-testid={`option-pudu-shop-${shop.shopId}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">
                        {shop.shopName || shop.shopId}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-2">
                        {shop.nbRobots} robot(s)
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">ID: {shop.shopId}</span>
                  </button>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialogOpen(false)}
                data-testid="btn-annuler-lier-pudu-shop"
              >
                Annuler
              </Button>
              <Button
                size="sm"
                disabled={!selectedShopId || lierMutation.isPending}
                onClick={handleConfirmLier}
                data-testid="btn-confirmer-lier-pudu-shop"
              >
                {lierMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <Link2 className="h-3.5 w-3.5 mr-1" />
                )}
                {puduShopId ? "Confirmer le remplacement" : "Lier"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelier} onOpenChange={setConfirmDelier}>
        <DialogContent className="max-w-sm" data-testid="dialog-confirm-delier-pudu-shop">
          <DialogHeader>
            <DialogTitle>Délier le magasin Pudu ?</DialogTitle>
            <DialogDescription>
              Le client ne sera plus rattaché à {currentShop?.shopName || puduShopId}. Cette
              action est réversible (vous pourrez relier plus tard).
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmDelier(false)}
              data-testid="btn-annuler-delier-pudu-shop"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={delierMutation.isPending}
              onClick={handleDelier}
              data-testid="btn-confirmer-delier-pudu-shop"
            >
              {delierMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Unlink className="h-3.5 w-3.5 mr-1" />
              )}
              Délier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── FacturesSection ───────────────────────────────────────────────────────────

const factureStatutConfig: Record<string, { label: string; className: string }> = {
  paid: {
    label: "Payée",
    className:
      "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400",
  },
  sent: {
    label: "En attente",
    className:
      "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400",
  },
  overdue: {
    label: "En retard",
    className:
      "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400",
  },
  draft: { label: "Brouillon", className: "bg-secondary text-muted-foreground" },
  void: { label: "Annulée", className: "bg-secondary text-muted-foreground" },
};

function FactureStatutBadge({ statut }: { statut: string | null }) {
  const cfg = statut
    ? factureStatutConfig[statut] ?? {
        label: statut,
        className: "bg-secondary text-muted-foreground",
      }
    : { label: "—", className: "bg-secondary text-muted-foreground" };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
}

function FacturesSection({ clientId }: { clientId: string }) {
  const { data: factures = [], isLoading } = useQuery<Facture[]>({
    queryKey: ["/api/factures/client", clientId],
    queryFn: () => authFetch(`/api/factures/client/${clientId}`).then((r) => r.json()),
  });

  const impayees = factures.filter((f) => f.statut && ["sent", "overdue"].includes(f.statut));
  const totalImpayeesTtc = impayees.reduce(
    (sum, f) => sum + parseFloat(f.montantTtc ?? "0"),
    0
  );

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Factures ({factures.length})</span>
      </div>

      {totalImpayeesTtc > 0 && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">
            Total impayé :{" "}
            {totalImpayeesTtc.toLocaleString("fr-FR", {
              style: "currency",
              currency: "EUR",
            })}
          </p>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground text-xs">Chargement...</p>
      ) : factures.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucune facture</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Numéro</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead className="text-right">Montant TTC</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {factures.map((f) => (
              <TableRow key={f.id} data-testid={`facture-row-${f.id}`}>
                <TableCell className="font-mono text-xs">{f.zohoNumero || "—"}</TableCell>
                <TableCell className="text-xs">
                  {f.dateFacture
                    ? format(new Date(f.dateFacture), "dd/MM/yyyy", { locale: fr })
                    : "—"}
                </TableCell>
                <TableCell className="text-xs">
                  {f.dateEcheance
                    ? format(new Date(f.dateEcheance), "dd/MM/yyyy", { locale: fr })
                    : "—"}
                </TableCell>
                <TableCell className="text-right text-xs font-medium">
                  {f.montantTtc
                    ? parseFloat(f.montantTtc).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "EUR",
                      })
                    : "—"}
                </TableCell>
                <TableCell>
                  <FactureStatutBadge statut={f.statut} />
                </TableCell>
                <TableCell>
                  {f.zohoPdfUrl && (
                    <a
                      href={f.zohoPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`btn-view-facture-${f.id}`}
                    >
                      <Button variant="ghost" size="sm" className="gap-1 text-xs">
                        <ExternalLink className="h-3 w-3" /> Voir dans Zoho
                      </Button>
                    </a>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── renderRobotForm ───────────────────────────────────────────────────────────

function renderRobotForm(
  form: typeof emptyRobotForm,
  setForm: (f: typeof emptyRobotForm) => void,
  onSubmit: (e: React.FormEvent) => void,
  isPending: boolean,
  submitLabel: string
) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Modèle *</Label>
          <Select value={form.modele} onValueChange={(v) => setForm({ ...form, modele: v })}>
            <SelectTrigger data-testid="select-robot-modele">
              <SelectValue placeholder="Sélectionner" />
            </SelectTrigger>
            <SelectContent>
              {robotModels.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>N° de série *</Label>
          <Input
            value={form.numeroSerie}
            onChange={(e) => setForm({ ...form, numeroSerie: e.target.value })}
            required
            data-testid="input-robot-serie"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Adresse MAC</Label>
          <Input
            value={form.macAddress}
            onChange={(e) => setForm({ ...form, macAddress: e.target.value })}
            placeholder="AA:BB:CC:DD:EE:FF"
            data-testid="input-robot-mac"
          />
        </div>
        <div>
          <Label>Statut</Label>
          <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
            <SelectTrigger data-testid="select-robot-statut">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROBOT_CLIENT_STATUSES.map((definition) => (
                <SelectItem key={definition.value} value={definition.value}>{definition.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Date d'achat</Label>
          <Input
            type="date"
            value={form.dateAchat}
            onChange={(e) => setForm({ ...form, dateAchat: e.target.value })}
            data-testid="input-robot-achat"
          />
        </div>
        <div>
          <Label>Date d'installation</Label>
          <Input
            type="date"
            value={form.dateInstallation}
            onChange={(e) => setForm({ ...form, dateInstallation: e.target.value })}
            data-testid="input-robot-installation"
          />
        </div>
        <div>
          <Label>Fin de garantie</Label>
          <Input
            type="date"
            value={form.garantieFin}
            onChange={(e) => setForm({ ...form, garantieFin: e.target.value })}
            data-testid="input-robot-garantie"
          />
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          data-testid="input-robot-notes"
        />
      </div>
      <Button
        type="submit"
        disabled={isPending || !form.modele || !form.numeroSerie}
        className="w-full"
        data-testid="btn-submit-robot"
      >
        {isPending ? "..." : submitLabel}
      </Button>
    </form>
  );
}

// ── RobotDetailView ───────────────────────────────────────────────────────────

function RobotDetailView({
  robot: initialRobot,
  onBack,
  onEdit,
  onDelete,
}: {
  robot: RobotClient;
  onBack: () => void;
  onEdit: (r: RobotClient) => void;
  onDelete: (id: string) => void;
}) {
  const [robot, setRobot] = useState(initialRobot);
  const { data: savHistory = [], isLoading: savLoading } = useRobotClientSavHistory(robot.id);
  const { data: modelImages = {} } = useRobotModelImages();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploadingPhotos, setUploadingPhotos] = useState<Record<string, boolean>>({});
  const modelImg = robot.imageUrl || modelImages[robot.modele] || null;

  const identityPhotoSlots = [
    { type: "front", label: "Face avant", urlKey: "photoFrontUrl" as const },
    { type: "back", label: "Face arrière", urlKey: "photoBackUrl" as const },
    { type: "left", label: "Côté gauche", urlKey: "photoLeftUrl" as const },
    { type: "right", label: "Côté droit", urlKey: "photoRightUrl" as const },
  ];

  const uploadPhoto = useCallback(
    async (photoType: string, file: File) => {
      const key = `${robot.id}_${photoType}`;
      setUploadingPhotos((prev) => ({ ...prev, [key]: true }));
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        const res = await fetch(`/api/robots-clients/${robot.id}/photos/${photoType}`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.message || "Upload échoué");
        }
        const data = await res.json();
        const fieldMap: Record<string, string> = {
          profile: "profilePhotoUrl",
          front: "photoFrontUrl",
          back: "photoBackUrl",
          left: "photoLeftUrl",
          right: "photoRightUrl",
        };
        setRobot((prev) => ({ ...prev, [fieldMap[photoType]]: data.url }));
        toast({ title: "Photo mise à jour" });
        queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      } catch (e: any) {
        console.error("[RobotDetailView] uploadPhoto error:", e);
        toast({ variant: "destructive", title: "Erreur upload", description: e.message });
      } finally {
        setUploadingPhotos((prev) => ({ ...prev, [key]: false }));
      }
    },
    [robot.id, toast, queryClient]
  );

  return (
    <div className="space-y-4 pt-2 text-sm">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="btn-back-from-robot-detail">
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(robot)}
            data-testid="btn-edit-robot"
          >
            <Pencil className="h-3 w-3 mr-1" /> Modifier
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              if (confirm("Supprimer ce robot ?")) onDelete(robot.id);
            }}
            data-testid="btn-delete-robot"
          >
            <Trash2 className="h-3 w-3 mr-1" /> Supprimer
          </Button>
        </div>
      </div>

      {(robot as any).aCompleter && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 px-3 py-2 flex items-center gap-2"
          data-testid={`banner-a-completer-${robot.id}`}
        >
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-200 text-amber-800 dark:bg-amber-800/60 dark:text-amber-300">
            À compléter
          </span>
          <span className="text-xs text-amber-800 dark:text-amber-300">
            Cette fiche robot a été créée depuis Pudu sans propriétaire résolu. Attribuez un
            client ou un revendeur pour la compléter.
          </span>
        </div>
      )}

      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Bot className="h-4 w-4" /> Identité
        </h3>
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-32">
            <PhotoUploadSlot
              label="Photo de profil"
              currentUrl={robot.profilePhotoUrl || modelImg}
              uploading={!!uploadingPhotos[`${robot.id}_profile`]}
              onUpload={(file) => uploadPhoto("profile", file)}
              testId="upload-robot-client-profile"
              aspect={1}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 flex-1">
            <div>
              <span className="text-muted-foreground">Modèle :</span> {robot.modele}
            </div>
            <div>
              <span className="text-muted-foreground">N° de série :</span>{" "}
              <span className="font-mono">{robot.numeroSerie}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Adresse MAC :</span>{" "}
              <span className="font-mono">{robot.macAddress || "—"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Statut :</span>{" "}
              <StatusBadge
                status={robot.statut}
                type="robotClient"
                label={getRobotClientStatus(robot.statut).label}
              />
            </div>
            <div>
              <span className="text-muted-foreground">Date d'achat :</span>{" "}
              {robot.dateAchat
                ? format(new Date(robot.dateAchat), "dd/MM/yyyy", { locale: fr })
                : "—"}
            </div>
            <div>
              <span className="text-muted-foreground">Date d'installation :</span>{" "}
              {robot.dateInstallation
                ? format(new Date(robot.dateInstallation), "dd/MM/yyyy", { locale: fr })
                : "—"}
            </div>
            <div>
              <span className="text-muted-foreground">Fin de garantie :</span>{" "}
              {robot.garantieFin
                ? format(new Date(robot.garantieFin), "dd/MM/yyyy", { locale: fr })
                : "—"}
            </div>
          </div>
        </div>
        {robot.notes && (
          <div className="mt-2">
            <span className="text-muted-foreground">Notes :</span> {robot.notes}
          </div>
        )}
      </div>

      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Camera className="h-4 w-4" /> Photos d'identité
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {identityPhotoSlots.map(({ type, label, urlKey }) => (
            <PhotoUploadSlot
              key={type}
              label={label}
              currentUrl={robot[urlKey] || null}
              uploading={!!uploadingPhotos[`${robot.id}_${type}`]}
              onUpload={(file) => uploadPhoto(type, file)}
              testId={`upload-robot-client-${type}`}
              aspect={16 / 9}
            />
          ))}
        </div>
      </div>

      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Historique SAV</h3>
          <Button
            size="sm"
            variant="outline"
            data-testid="btn-creer-dossier-sav"
            onClick={() => {
              const params = new URLSearchParams({
                prefill: "1",
                origine: "client",
                clientId: robot.clientId || "",
                robotClientId: robot.id,
                sn: robot.numeroSerie || "",
                model: robot.modele || "",
              });
              setLocation(`/sav?${params.toString()}`);
            }}
          >
            <ShieldAlert className="w-3.5 h-3.5 mr-1" />
            Créer dossier SAV
          </Button>
        </div>
        {savLoading ? (
          <p className="text-muted-foreground text-xs">Chargement...</p>
        ) : savHistory.length === 0 ? (
          <p className="text-muted-foreground text-xs">Aucune intervention enregistrée</p>
        ) : (
          <div className="space-y-2">
            {savHistory.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between py-2 px-3 rounded bg-muted/30"
                data-testid={`sav-history-${d.id}`}
              >
                <div>
                  <span className="font-mono text-xs font-medium">{d.reference}</span>
                  <span className="text-muted-foreground ml-2 text-xs">
                    {d.dateCreation
                      ? format(new Date(d.dateCreation), "dd/MM/yyyy", { locale: fr })
                      : ""}
                  </span>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {d.problematique}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge
                    status={d.statut}
                    type="sav"
                    label={
                      {
                        ouvert: "Ouvert",
                        en_cours: "En cours",
                        en_attente_pieces: "En attente pièces",
                        resolu: "Résolu",
                        cloture: "Clôturé",
                      }[d.statut]
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLocation("/sav")}
                    data-testid={`btn-sav-link-${d.id}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-3">Historique des missions</h3>
        <p className="text-muted-foreground text-xs">Fonctionnalité à venir</p>
      </div>
    </div>
  );
}
